import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  HomeIcon,
  CubeIcon,
  UsersIcon,
  CalendarIcon,
  ChartBarIcon,
  XMarkIcon,
  CogIcon,
  ClockIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

const navigation = [
  { name: 'Dashboard', href: 'dashboard', icon: HomeIcon },
  { name: 'Inventory', href: 'inventory', icon: CubeIcon },
  { name: 'Customers', href: 'customers', icon: UsersIcon },
  { name: 'Reservations', href: 'reservations', icon: CalendarIcon },
  { name: 'Timesheets', href: 'timesheets', icon: ClockIcon, employeeOnly: true },
  { name: 'Employees', href: 'employees', icon: UserIcon, adminOnly: true },
  { name: 'Reports', href: 'reports', icon: ChartBarIcon },
  { name: 'Settings', href: 'storefront', icon: CogIcon, adminOnly: true },
];

const Sidebar = ({ open, setOpen }) => {
  const { user, isAdmin, canViewSettings, canViewEmployees } = useAuth();
  const navigate = useNavigate();

  const handleNavClick = (href) => {
    console.log('Navigation clicked:', href);
    navigate(href);
    setOpen(false);
  };

  // Handle ESC key to close sidebar
  React.useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && open) {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [open, setOpen]);

  console.log('Sidebar render - user:', user);

  return (
    <>
      {/* Mobile sidebar overlay */}
      <div className={`fixed inset-0 flex z-40 lg:hidden ${open ? '' : 'hidden'}`}>
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity" 
          onClick={() => setOpen(false)} 
        />
        
        {/* Sidebar panel */}
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white shadow-xl transform transition-transform duration-300 ease-in-out">
          {/* Close button */}
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white hover:bg-gray-600 touch-friendly"
              onClick={() => setOpen(false)}
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          
          {/* Mobile sidebar content */}
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4 mb-5">
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900">Navigation</h1>
            </div>
            <nav className="px-2 space-y-1">
              {navigation.map((item) => {
                // Use permission-based access control for Settings
                if (item.name === 'Settings' && !canViewSettings) return null;
                // Use permission-based access control for Employees  
                if (item.name === 'Employees' && !canViewEmployees) return null;
                // Keep legacy checks for other admin-only items
                if (item.adminOnly && item.name !== 'Settings' && item.name !== 'Employees' && !isAdmin) return null;
                if (item.employeeOnly && user?.role !== 'EMPLOYEE') return null;
                
                return (
                  <button
                    key={item.name}
                    onClick={() => handleNavClick(item.href)}
                    className="mobile-nav-item w-full group flex items-center text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors duration-150 touch-friendly"
                  >
                    <item.icon className="mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500 flex-shrink-0" />
                    <span className="truncate">{item.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Desktop sidebar overlay */}
      <div className={`${open ? 'hidden lg:flex' : 'hidden'} lg:fixed lg:inset-0 lg:z-40`}>
        {/* Desktop background overlay */}
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity" 
          onClick={() => setOpen(false)} 
        />
        
        {/* Desktop sidebar panel */}
        <div className="relative flex flex-col w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out">
          {/* Close button for desktop */}
          <div className="absolute top-4 right-4 z-50">
            <button
              type="button"
              className="flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-300 hover:bg-gray-100 bg-white shadow-sm"
              onClick={() => setOpen(false)}
            >
              <XMarkIcon className="h-6 w-6 text-gray-500" />
            </button>
          </div>
          
          <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4 h-16 border-b border-gray-200">
              <h1 className="text-xl font-semibold text-gray-900">Navigation</h1>
            </div>
          <nav className="flex-1 px-2 py-4 space-y-1">
            {navigation.map((item) => {
              // Use permission-based access control for Settings
              if (item.name === 'Settings' && !canViewSettings) return null;
              // Use permission-based access control for Employees  
              if (item.name === 'Employees' && !canViewEmployees) return null;
              // Keep legacy checks for other admin-only items
              if (item.adminOnly && item.name !== 'Settings' && item.name !== 'Employees' && !isAdmin) return null;
              if (item.employeeOnly && user?.role !== 'EMPLOYEE') return null;
              
              return (
                <button
                  key={item.name}
                  onClick={() => handleNavClick(item.href)}
                  className="w-full group flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors duration-150"
                >
                  <item.icon className="mr-3 h-6 w-6 text-gray-400 group-hover:text-gray-500 flex-shrink-0" />
                  <span className="truncate">{item.name}</span>
                </button>
              );
            })}
          </nav>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;